import { useMemo, useState } from 'react';
import {
	SimpleForm,
	TextInput,
	DateInput,
	SelectInput,
	ReferenceArrayInput,
	ReferenceInput,
	AutocompleteInput,
	BooleanInput,
	required,
	RadioButtonGroupInput,
	useGetList,
	useRecordContext,
} from 'react-admin';
import { useController, useFieldArray, useFormContext, useWatch } from 'react-hook-form';
import { Box, Button, FormControlLabel, Stack, Switch, Tab, Tabs, Tooltip, Typography } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { DefaultValues, PersonaChoices } from './meta';

const visibilityChoices = [
	{ id: 1, name: 'All Users' },
	{ id: 0, name: 'Specific Groups' }
];

const SessionVisibilityInput = () => {
	const publicValue = parseInt( useWatch({ name: 'public' }), 10 );

	return (
		<>
			<RadioButtonGroupInput
				source="public"
				label="Session Visibility"
				choices={visibilityChoices}
			/>
			{publicValue === 0 && (
				<ReferenceArrayInput reference="groups" source="group_ids" label="Groups" />
			)}
		</>
	);
};

const PersonaInput = () => (
	<SelectInput source="persona_id" label="Persona" validate={required()} choices={PersonaChoices} />
);

export const StatusToggleInput = () => {
	const { field } = useController({
		name: 'status',
		defaultValue: 'active',
		rules: { required: true }
	});
	const checked = field.value !== 'inactive';

	return (
		<FormControlLabel
			label="Active"
			control={
				<Switch
					checked={checked}
					onChange={(event) => field.onChange(event.target.checked ? 'active' : 'inactive')}
					onBlur={field.onBlur}
				/>
			}
		/>
	);
};

const SessionMetaInputs = () => (
	<Box
		sx={{
			display: 'grid',
			gridTemplateColumns: {
				xs: '1fr',
				sm: 'repeat(2, minmax(180px, 1fr))',
				lg: 'repeat(4, minmax(160px, 1fr))'
			},
			gap: 2,
			width: '100%',
			maxWidth: 960,
			alignItems: 'start'
		}}
	>
		<PersonaInput />
		<DateInput source="start_date" label="Start Date" />
	</Box>
);

const SessionTitleInputs = () => (
	<TextInput source="name" validate={required()} />
);

function emptySessionModule() {
	return { module_id: undefined, active: true, context: '', persona_context: '', label: '' };
}

const ModuleTabLabel = ({ index, moduleById }) => {
	const moduleId = useWatch({ name: `session_modules.${index}.module_id` });
	const active = useWatch({ name: `session_modules.${index}.active` });
	const label = useWatch({ name: `session_modules.${index}.label` });
	const module = moduleById[ moduleId ];
	const isActive = active === undefined || active === true || Number( active ) === 1;
	const isModuleInactive = module && Number( module.active ) !== 1;
	const title = label?.trim() || module?.title || 'New Module';

	return (
		<Stack direction="row" spacing={1} alignItems="center">
			{isModuleInactive ? (
				<Tooltip title="This module is globally inactive and won't appear in the user experience">
					<WarningAmberIcon fontSize="small" color={isActive ? 'warning' : 'disabled'} />
				</Tooltip>
			) : (
				<Box
					sx={{
						width: 8,
						height: 8,
						borderRadius: '50%',
						flexShrink: 0,
						bgcolor: isActive ? 'success.main' : 'text.disabled'
					}}
				/>
			)}
			<span>{title}</span>
		</Stack>
	);
};

const SESSION_MODULE_FIELD_KEYS = [ 'module_id', 'active', 'label', 'context', 'persona_context' ];

function normalizeSessionModuleField( key, value ) {
	if ( key === 'active' ) {
		return Number( value ) ? 1 : 0;
	}
	if ( key === 'module_id' ) {
		return value === undefined || value === null || value === '' ? '' : String( value );
	}
	return value || '';
}

// Reverts a single module tab to its last-saved values, identified by database id rather
// than array position - drag-and-drop reordering means position alone can't be trusted.
const ModuleTabCancelButton = ({ index }) => {
	const { setValue } = useFormContext();
	const record = useRecordContext();
	const watched = useWatch({ name: `session_modules.${index}` }) || {};
	const original = useMemo(() => {
		if ( watched.id === undefined || watched.id === null ) {
			return null;
		}
		return ( record?.session_modules || [] ).find( item => String( item.id ) === String( watched.id ) ) || null;
	}, [ record, watched.id ]);

	if ( !original ) {
		return null;
	}

	const isDirty = SESSION_MODULE_FIELD_KEYS.some(
		key => normalizeSessionModuleField( key, watched[key] ) !== normalizeSessionModuleField( key, original[key] )
	);

	const handleCancel = () => {
		SESSION_MODULE_FIELD_KEYS.forEach( key => {
			setValue( `session_modules.${index}.${key}`, original[key], { shouldDirty: true } );
		});
	};

	return (
		<Button variant="outlined" size="small" onClick={handleCancel} disabled={!isDirty}>
			Cancel Changes
		</Button>
	);
};

const SessionModulesInput = () => {
	const { fields, append, remove, move } = useFieldArray({ name: 'session_modules' });
	const [ activeIndex, setActiveIndex ] = useState( 0 );
	const [ dragIndex, setDragIndex ] = useState( null );
	const { data: modules = [] } = useGetList( 'modules', {
		pagination: { page: 1, perPage: 200 },
		sort: { field: 'title', order: 'ASC' }
	});
	const moduleById = Object.fromEntries( modules.map( module => [ String( module.id ), module ] ) );

	const index = Math.min( activeIndex, Math.max( fields.length - 1, 0 ) );

	const handleChange = ( event, value ) => {
		if ( value === '__add__' ) {
			append( emptySessionModule() );
			setActiveIndex( fields.length );
			// The freshly mounted Module field can end up with browser focus, and
			// react-admin's AutocompleteInput opens its choice list on focus - blur
			// once the DOM settles so a new tab doesn't appear with the list open.
			requestAnimationFrame( () => {
				if ( document.activeElement instanceof HTMLElement ) {
					document.activeElement.blur();
				}
			});
			return;
		}
		setActiveIndex( value );
	};

	const handleRemove = () => {
		remove( index );
		setActiveIndex( Math.max( index - 1, 0 ) );
	};

	const handleDragStart = ( i ) => () => setDragIndex( i );
	const handleDragOver = ( event ) => event.preventDefault();
	const handleDrop = ( i ) => ( event ) => {
		event.preventDefault();
		if ( dragIndex === null || dragIndex === i ) {
			return;
		}
		move( dragIndex, i );
		setActiveIndex( i );
		setDragIndex( null );
	};
	const handleDragEnd = () => setDragIndex( null );

	return (
		<Box sx={{ width: '100%', maxWidth: 720, mt: 2 }}>
			<Typography variant="h6">Modules</Typography>
			<Tabs
				value={fields.length ? index : false}
				onChange={handleChange}
				variant="scrollable"
				scrollButtons="auto"
				allowScrollButtonsMobile
				sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
			>
				{fields.map( ( field, i ) => (
					<Tab
						key={field.id}
						value={i}
						label={<ModuleTabLabel index={i} moduleById={moduleById} />}
						draggable
						onDragStart={handleDragStart( i )}
						onDragOver={handleDragOver}
						onDrop={handleDrop( i )}
						onDragEnd={handleDragEnd}
						sx={{ opacity: dragIndex === i ? 0.4 : 1, cursor: 'grab' }}
					/>
				))}
				<Tab label="+" value="__add__" />
			</Tabs>
			{fields.length > 0 ? (
				<Box>
					<ReferenceInput source={`session_modules.${index}.module_id`} reference="modules">
						<AutocompleteInput
							label="Select a module"
							optionText="title"
							validate={required()}
							variant="outlined"
							fullWidth
						/>
					</ReferenceInput>
					<BooleanInput source={`session_modules.${index}.active`} label="Active" defaultValue={true} />
					<TextInput source={`session_modules.${index}.label`} label="Label" helperText="Optionally override the module label" fullWidth />
					<TextInput source={`session_modules.${index}.context`} label="Conversation Context" multiline fullWidth />
					<TextInput source={`session_modules.${index}.persona_context`} label="Persona Context" multiline fullWidth />
					<Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 1 }}>
						<ModuleTabCancelButton index={index} />
						<Button variant="outlined" size="small" color="error" startIcon={<DeleteIcon />} onClick={handleRemove}>
							Remove Module
						</Button>
					</Box>
				</Box>
			) : (
				<Typography variant="body2" color="text.secondary">
					No modules attached yet. Click &ldquo;+&rdquo; to add one.
				</Typography>
			)}
		</Box>
	);
};

export const SessionFormFields = (
	<>
		<SessionTitleInputs />
		<TextInput source="public_name" label="Public Session Name" validate={required()} />
		<SessionMetaInputs />
		<SessionVisibilityInput />
		<SessionModulesInput />
	</>
);

export const FormFields = (
	<SimpleForm defaultValues={DefaultValues}>
		{SessionFormFields}
	</SimpleForm>
);
